import cx from 'classnames';
import {
  getCountryOptions,
  getDepartmentOptions,
  getStateOptions,
} from 'components/admin/datasets';
import { usePopulationAttributes } from 'components/admin/hooks';
import { IPopulation } from 'components/admin/types';
import { Form, ISelectOption } from 'components/admin/ui';
import { ILocation, UserProfileRole } from 'lib/types/user';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import css from './PopulationFilters.module.scss';

interface IPopulationFiltersProps {
  onPopulationChange: (population: IPopulation) => void;
  initialPopulation?: IPopulation;
  population: IPopulation;
}

export const PopulationFilters = ({
  onPopulationChange,
  initialPopulation = {},
  population,
}: IPopulationFiltersProps) => {
  // For React Select only.
  const [countries, setCountries] = useState<ISelectOption<ILocation>[]>();
  const [states, setStates] = useState<ISelectOption<ILocation>[]>();
  const [departments, setDepartments] = useState<ISelectOption<string>[]>();

  useEffect(() => {
    setCountries(getCountryOptions(initialPopulation.locations));
    setDepartments(getDepartmentOptions(initialPopulation.departments));
    setStates(getStateOptions(initialPopulation.locations));
  }, [JSON.stringify(initialPopulation)]);

  const { data: populationGroups } = usePopulationAttributes({});

  const getValuesWithoutAll: <T>(selected: ISelectOption<T>[]) => T[] =
    _.flowRight([
      _.partialRight(_.map, 'value'),
      _.partialRight(_.reject, { value: 'all' }),
    ]);

  /**
   * Non-USA country selected, USA selected, states in population => set
   * countries, set states, remove USA.
   *
   * All other scenarios => set countries.
   */
  const handleCountriesChange = (selected: ISelectOption<ILocation>[]) => {
    const countries = getValuesWithoutAll(selected);
    // Get rid of all the countries in population list.
    const states = _.reject(population.locations, (l) => !l.state);
    const isUsaSelected = _.some(countries, { country: 'USA' });
    // Whether to keep the states or not.
    const locations =
      isUsaSelected && !_.isEmpty(states)
        ? [...states, ..._.reject(countries, { country: 'USA' })]
        : countries;
    onPopulationChange({ ...population, locations });
  };

  /**
   * All states deselected => add USA.
   *
   * All other scenarios => set countries, set states, remove USA.
   */
  const handleStatesChange = (selected: ISelectOption<ILocation>[]) => {
    const states = getValuesWithoutAll(selected);
    // Get rid of all the states in population list.
    const countries = _.reject(population.locations, (l) => !!l.state);
    // Add USA back if we unselect all the states.
    const locations = _.isEmpty(states)
      ? [...states, ...countries, { country: 'USA' }]
      : [...states, ..._.reject(countries, { country: 'USA' })];
    onPopulationChange({ ...population, locations });
  };

  const handleDepartmentsChange = (selected: ISelectOption<string>[]) =>
    onPopulationChange({
      ...population,
      departments: getValuesWithoutAll(selected),
    });

  const countryOptions = getCountryOptions(populationGroups?.locations, true);
  const stateOptions = getStateOptions(populationGroups?.locations, true);
  const departmentOptions = _.concat(
    { label: 'All Departments', value: 'all' },
    getDepartmentOptions(populationGroups?.departments)
  );

  const isUsaSelected = _.some(countries, (c) => c.value.country === 'USA');

  return (
    <>
      <Form.Group className={css.selectFilters}>
        <div className={css.selectLocation}>
          <Form.CheckboxMultiSelect
            className={cx(css.countries, {
              [css.small]: isUsaSelected,
            })}
            // Do not remove this. I don't know why, but without it the entire
            // selector breaks when prepopulating the list. Took forever to
            // discover this solution.
            getOptionValue={(option: ISelectOption) => option.label}
            isFluidWidth
            multiValueLabel="Countries"
            onChange={(selected: ISelectOption<ILocation>[]) => {
              handleCountriesChange(selected);
              setCountries(selected);
              if (!_.some(selected, { value: 'USA' })) {
                setStates([]);
              }
            }}
            options={countryOptions}
            placeholder="Select Countries"
            value={countries}
          />
          {isUsaSelected && (
            <Form.CheckboxMultiSelect
              className={css.states}
              getOptionValue={(option: ISelectOption) => option.label}
              isFluidWidth
              multiValueLabel="States"
              onChange={(selected: ISelectOption<ILocation>[]) => {
                handleStatesChange(selected);
                setStates(selected);
              }}
              options={stateOptions}
              placeholder="Select States"
              value={states}
            />
          )}
        </div>
        <Form.CheckboxMultiSelect
          className={css.departments}
          isFluidWidth
          multiValueLabel="Departments"
          onChange={(selected: ISelectOption<string>[]) => {
            handleDepartmentsChange(selected);
            setDepartments(selected);
          }}
          options={departmentOptions}
          placeholder="Select Departments"
          value={departments}
        />
      </Form.Group>

      <Form.Group as="fieldset" className="my-0">
        <div className={css.roles}>
          <Form.Radio
            checked={!population.roles}
            label="All"
            onChange={() =>
              onPopulationChange({ ...population, roles: undefined })
            }
            variant="bordered"
          />
          <Form.Radio
            checked={_.includes(population.roles, UserProfileRole.MANAGER)}
            label="Managers"
            onChange={() =>
              onPopulationChange({
                ...population,
                roles: [UserProfileRole.MANAGER],
              })
            }
            variant="bordered"
          />
          <Form.Radio
            checked={_.includes(population.roles, UserProfileRole.NONMANAGER)}
            label="Non-managers"
            onChange={() =>
              onPopulationChange({
                ...population,
                roles: [UserProfileRole.NONMANAGER],
              })
            }
            variant="bordered"
          />
        </div>
      </Form.Group>
    </>
  );
};
